import { useEffect, useMemo, useState } from "react";
import {
    Search, CreditCard, CheckCircle2, XCircle, Clock3, IndianRupee,
    TrendingUp, Download, ChevronDown, Calendar, Loader2, AlertCircle, RotateCcw,
} from "lucide-react";
import { getExpertBookings } from "../service/courseService.jsx";

// Change this if expertId should come from auth/context instead of being fixed.
const EXPERT_ID = 2;

/* ---------------------------------------------------------
   Design tokens
--------------------------------------------------------- */
const C = {
    bg: "#F4F5FA",
    card: "#FFFFFF",
    border: "#E7E8F0",
    indigo: "#5B4FE5",
    indigoSoft: "#EDEBFC",
    indigoDeep: "#443AC4",
    green: "#1E9E6B",
    greenSoft: "#E3F7EE",
    red: "#E1483F",
    redSoft: "#FCEAE9",
    amber: "#C6820E",
    amberSoft: "#FBF0DC",
    blue: "#3B6FE0",
    blueSoft: "#E8EEFC",
    ink: "#1B1D2A",
    slate: "#6B7280",
    slateLight: "#9CA3AF",
};

const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Courier New', monospace";

const AVATAR_COLORS = ["#5B4FE5", "#3B6FE0", "#1E9E6B", "#C6820E", "#E1483F", "#7C5CE0"];

/* ---------------------------------------------------------
   Booking-status buckets (from bookingStatus)
--------------------------------------------------------- */
const HISTORY_STATUSES = new Set(["COMPLETED", "CANCELLED", "REJECTED"]);

const STATUS_META = {
    completed: { label: "Completed", color: C.green, soft: C.greenSoft, icon: CheckCircle2 },
    upcoming: { label: "Upcoming", color: C.blue, soft: C.blueSoft, icon: Clock3 },
    confirmed: { label: "Confirmed", color: C.indigo, soft: C.indigoSoft, icon: Clock3 },
    pending: { label: "Pending", color: C.amber, soft: C.amberSoft, icon: Clock3 },
    cancelled: { label: "Cancelled", color: C.red, soft: C.redSoft, icon: XCircle },
    rejected: { label: "Rejected", color: C.red, soft: C.redSoft, icon: XCircle },
};

/* ---------------------------------------------------------
   Payment-status meta (from paymentStatus — replaces the old
   "method" concept, since the API doesn't return a payment
   method field, only a payment status).
--------------------------------------------------------- */
const PAYMENT_META = {
    captured: { label: "Captured", color: C.green, soft: C.greenSoft },
    pending: { label: "Pending", color: C.amber, soft: C.amberSoft },
    failed: { label: "Failed", color: C.red, soft: C.redSoft },
    refunded: { label: "Refunded", color: C.blue, soft: C.blueSoft },
};

function initials(name) {
    return (name || "")
        .replace(/^(Dr\.|Prof\.)\s*/i, "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "??";
}

function fmtAmount(n) {
    return (Number(n) || 0).toLocaleString("en-IN");
}

/* ---------------------------------------------------------
   Date helpers — bookingDate is "YYYY-MM-DD", so string
   comparisons work directly; Date objects used for month math.
--------------------------------------------------------- */
function todayStr() {
    return new Date().toISOString().slice(0, 10);
}
function yesterdayStr() {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
}
function formatDateLabel(isoDate) {
    if (!isoDate) return "—";
    return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
function formatTimeRange(isoDate, timeStr, durationMinutes) {
    if (!isoDate || !timeStr) return "—";
    const [h = "0", m = "0", s = "0"] = timeStr.split(":");
    const start = new Date(`${isoDate}T00:00:00`);
    start.setHours(Number(h), Number(m), Number(s) || 0, 0);
    const fmt = (d) => d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
    if (!durationMinutes) return fmt(start);
    const end = new Date(start.getTime() + durationMinutes * 60000);
    return `${fmt(start)} – ${fmt(end)}`;
}

/* ---------------------------------------------------------
   Map an API booking record -> the flat shape the dashboard uses.
--------------------------------------------------------- */
function normalizeBooking(b) {
    const bookingStatus = (b.bookingStatus || "").toUpperCase();
    const paymentStatus = (b.paymentStatus || "").toLowerCase();
    return {
        id: b.bookingId,
        user: b.fullName,
        course: b.message?.trim() || "Consultation",
        expertName: b.expertName,
        dateIso: b.bookingDate,
        date: formatDateLabel(b.bookingDate),
        time: formatTimeRange(b.bookingDate, b.bookingTime, b.durationMinutes),
        amount: Number(b.totalAmount ?? b.consultationFee) || 0,
        status: bookingStatus.toLowerCase(),
        paymentStatus,
        bucket: HISTORY_STATUSES.has(bookingStatus) ? "history" : "upcoming",
    };
}

/* ---------------------------------------------------------
   Small building blocks
--------------------------------------------------------- */
function StatCard({ label, value, icon: Icon, tint, sub }) {
    return (
        <div
            style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: "14px",
                padding: "20px 22px",
                flex: 1,
                minWidth: "190px",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <span style={{ fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, letterSpacing: "0.01em" }}>
                    {label}
                </span>
                <div
                    style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        background: tint.soft,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon size={15} color={tint.color} />
                </div>
            </div>
            <div style={{ fontFamily: MONO, fontSize: "26px", fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
                {value}
            </div>
            {sub && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight, marginTop: "5px" }}>{sub}</div>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || { label: status || "Unknown", color: C.slate, soft: "#F1F2F6", icon: Clock3 };
    const Icon = meta.icon;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontFamily: SANS,
                fontSize: "12px",
                fontWeight: 600,
                color: meta.color,
                background: meta.soft,
                padding: "5px 11px",
                borderRadius: "999px",
            }}
        >
            <Icon size={12} /> {meta.label}
        </span>
    );
}

function PaymentTag({ status }) {
    const meta = PAYMENT_META[status] || { label: status || "—", color: C.slate, soft: "#F1F2F6" };
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontFamily: SANS, fontSize: "13px", color: C.ink }}>
            <span
                style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "7px",
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CreditCard size={13} color={meta.color} />
            </span>
            {meta.label}
        </span>
    );
}

/* ---------------------------------------------------------
   Payment-status breakdown card (replaces old method breakdown)
--------------------------------------------------------- */
function PaymentBreakdown({ data }) {
    const totals = useMemo(() => {
        const byStatus = {};
        let sum = 0;
        data.forEach((r) => {
            byStatus[r.paymentStatus] = (byStatus[r.paymentStatus] || 0) + r.amount;
            sum += r.amount;
        });
        return { byStatus, sum };
    }, [data]);

    const order = ["captured", "pending", "failed", "refunded"];

    return (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "20px 22px", flex: 1, minWidth: "260px" }}>
            <div style={{ fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, marginBottom: "16px" }}>
                PAYMENT STATUS
            </div>
            <div style={{ display: "flex", height: "9px", borderRadius: "999px", overflow: "hidden", marginBottom: "16px", background: "#F1F2F6" }}>
                {order.map((k) => {
                    const pct = totals.sum ? (totals.byStatus[k] || 0) / totals.sum * 100 : 0;
                    return pct > 0 ? <div key={k} style={{ width: `${pct}%`, background: PAYMENT_META[k].color }} /> : null;
                })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                {order.map((k) => {
                    const amt = totals.byStatus[k] || 0;
                    const pct = totals.sum ? Math.round((amt / totals.sum) * 100) : 0;
                    return (
                        <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: PAYMENT_META[k].color }} />
                                <span style={{ fontFamily: SANS, fontSize: "12.5px", color: C.ink }}>{PAYMENT_META[k].label}</span>
                            </div>
                            <span style={{ fontFamily: MONO, fontSize: "12.5px", color: C.slate }}>
                                ₹{fmtAmount(amt)} · {pct}%
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------
   Main dashboard
--------------------------------------------------------- */
const TABS = [
    { key: "all", label: "All" },
    { key: "completed", label: "Completed" },
    { key: "upcoming", label: "Upcoming" },
    { key: "confirmed", label: "Confirmed" },
    { key: "pending", label: "Pending" },
    { key: "cancelled", label: "Cancelled" },
];

const DATE_TABS = [
    { key: "all", label: "All time" },
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "month", label: "This month" },
];

export default function PaymentDashboard() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [tab, setTab] = useState("all");
    const [paymentFilter, setPaymentFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [dateFilter, setDateFilter] = useState("all");
    const [showCustom, setShowCustom] = useState(false);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    const loadBookings = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const response = await getExpertBookings(EXPERT_ID);
            const list = Array.isArray(response) ? response : response?.data || [];
            setBookings(list.map(normalizeBooking));
        } catch (err) {
            console.error("Failed to fetch bookings:", err);
            setLoadError(err.message || "Failed to load payments. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBookings();
    }, []);

    const passesDateFilter = (r) => {
        if (!r.dateIso) return dateFilter === "all";
        if (dateFilter === "today") return r.dateIso === todayStr();
        if (dateFilter === "yesterday") return r.dateIso === yesterdayStr();
        if (dateFilter === "month") {
            const now = new Date();
            const d = new Date(`${r.dateIso}T00:00:00`);
            return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }
        if (dateFilter === "custom") {
            if (!customFrom && !customTo) return true;
            if (customFrom && r.dateIso < customFrom) return false;
            if (customTo && r.dateIso > customTo) return false;
            return true;
        }
        return true;
    };

    const filtered = useMemo(() => {
        return bookings.filter((r) => {
            if (tab !== "all" && r.status !== tab) return false;
            if (paymentFilter !== "all" && r.paymentStatus !== paymentFilter) return false;
            if (query && !`${r.user} ${r.course}`.toLowerCase().includes(query.toLowerCase())) return false;
            if (!passesDateFilter(r)) return false;
            return true;
        });
    }, [bookings, tab, paymentFilter, query, dateFilter, customFrom, customTo]);

    const dateLabel =
        dateFilter === "custom"
            ? customFrom || customTo
                ? `${customFrom || "…"} → ${customTo || "…"}`
                : "Custom range"
            : DATE_TABS.find((d) => d.key === dateFilter)?.label;

    const stats = useMemo(() => {
        const paid = bookings.filter((r) => r.status !== "cancelled" && r.status !== "rejected").reduce((s, r) => s + r.amount, 0);
        const completed = bookings.filter((r) => r.status === "completed").length;
        const cancelled = bookings.filter((r) => r.status === "cancelled" || r.status === "rejected").length;
        const upcoming = bookings.filter((r) => r.status === "upcoming" || r.status === "confirmed" || r.status === "pending").length;
        return { paid, completed, cancelled, upcoming };
    }, [bookings]);

    return (
        <div style={{ height: "100%", overflowY: "auto", background: C.bg, padding: "36px 24px 60px", fontFamily: SANS, boxSizing: "border-box" }}>
            <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
                    <div>
                        <div style={{ fontFamily: SANS, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", color: C.indigo, marginBottom: "6px" }}>
                            PAYMENTS
                        </div>
                        <h1 style={{ fontFamily: SANS, fontSize: "26px", fontWeight: 700, color: C.ink, margin: 0 }}>
                            Payment dashboard
                        </h1>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            onClick={loadBookings}
                            disabled={loading}
                            style={{
                                display: "flex", alignItems: "center", gap: "7px",
                                fontFamily: SANS, fontWeight: 600, fontSize: "13px", color: C.ink,
                                background: C.card, border: `1px solid ${C.border}`, borderRadius: "8px",
                                padding: "10px 16px", cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1,
                            }}
                        >
                            {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Refresh
                        </button>
                        <button
                            style={{
                                display: "flex", alignItems: "center", gap: "7px",
                                fontFamily: SANS, fontWeight: 600, fontSize: "13px", color: C.ink,
                                background: C.card, border: `1px solid ${C.border}`, borderRadius: "8px",
                                padding: "10px 16px", cursor: "pointer",
                            }}
                        >
                            <Download size={14} /> Export
                        </button>
                    </div>
                </div>

                {loadError && (
                    <div style={{
                        display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px",
                        background: C.redSoft, border: `1px solid ${C.red}30`, color: C.red,
                        borderRadius: "10px", padding: "12px 16px", fontFamily: SANS, fontSize: "13px",
                    }}>
                        <AlertCircle size={18} />
                        <span>{loadError}</span>
                    </div>
                )}

                {/* Stat cards */}
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                    <StatCard label="TOTAL PAID" value={`₹${fmtAmount(stats.paid)}`} icon={IndianRupee} tint={{ color: C.indigo, soft: C.indigoSoft }} sub={`${bookings.length} transactions`} />
                    <StatCard label="COMPLETED" value={stats.completed} icon={CheckCircle2} tint={{ color: C.green, soft: C.greenSoft }} sub="Payments settled" />
                    <StatCard label="UPCOMING / CONFIRMED" value={stats.upcoming} icon={Clock3} tint={{ color: C.blue, soft: C.blueSoft }} sub="Scheduled sessions" />
                    <StatCard label="CANCELLED" value={stats.cancelled} icon={XCircle} tint={{ color: C.red, soft: C.redSoft }} sub="Refund issued" />
                </div>

                {/* Payment breakdown + trend strip */}
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
                    <PaymentBreakdown data={bookings} />
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "20px 22px", flex: 1, minWidth: "260px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <div style={{ fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, marginBottom: "10px" }}>
                            TOTAL COLLECTED
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                            <span style={{ fontFamily: MONO, fontSize: "26px", fontWeight: 700, color: C.ink }}>
                                ₹{fmtAmount(stats.paid)}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: "3px", fontFamily: SANS, fontSize: "12px", fontWeight: 700, color: C.green }}>
                                <TrendingUp size={13} />
                            </span>
                        </div>
                        <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight, marginTop: "6px" }}>
                            across {bookings.length} bookings
                        </div>
                    </div>
                </div>

                {/* Filters + table */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", overflow: "hidden" }}>
                    {/* Date filter row */}
                    <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, marginRight: "4px" }}>
                                <Calendar size={14} color={C.slateLight} /> Date
                            </span>
                            {DATE_TABS.map((d) => (
                                <button
                                    key={d.key}
                                    onClick={() => { setDateFilter(d.key); setShowCustom(false); }}
                                    style={{
                                        fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                                        padding: "7px 13px", borderRadius: "999px", cursor: "pointer",
                                        border: `1px solid ${dateFilter === d.key ? C.indigo : C.border}`,
                                        color: dateFilter === d.key ? C.indigo : C.slate,
                                        background: dateFilter === d.key ? C.indigoSoft : "transparent",
                                    }}
                                >
                                    {d.label}
                                </button>
                            ))}
                            <button
                                onClick={() => { setDateFilter("custom"); setShowCustom((s) => !s); }}
                                style={{
                                    fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                                    padding: "7px 13px", borderRadius: "999px", cursor: "pointer",
                                    border: `1px solid ${dateFilter === "custom" ? C.indigo : C.border}`,
                                    color: dateFilter === "custom" ? C.indigo : C.slate,
                                    background: dateFilter === "custom" ? C.indigoSoft : "transparent",
                                }}
                            >
                                Custom date {dateFilter === "custom" ? `· ${dateLabel}` : ""}
                            </button>

                            {dateFilter !== "all" && (
                                <button
                                    onClick={() => { setDateFilter("all"); setShowCustom(false); setCustomFrom(""); setCustomTo(""); }}
                                    style={{
                                        fontFamily: SANS, fontSize: "12px", fontWeight: 600, color: C.slateLight,
                                        background: "none", border: "none", cursor: "pointer", marginLeft: "2px",
                                    }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        {showCustom && dateFilter === "custom" && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                                <div>
                                    <label style={{ display: "block", fontFamily: SANS, fontSize: "11px", fontWeight: 600, color: C.slateLight, marginBottom: "4px" }}>
                                        FROM
                                    </label>
                                    <input
                                        type="date"
                                        value={customFrom}
                                        onChange={(e) => setCustomFrom(e.target.value)}
                                        style={{
                                            fontFamily: SANS, fontSize: "12.5px", padding: "8px 10px",
                                            borderRadius: "7px", border: `1px solid ${C.border}`, background: C.bg, outline: "none",
                                        }}
                                    />
                                </div>
                                <div style={{ color: C.slateLight, marginTop: "18px" }}>→</div>
                                <div>
                                    <label style={{ display: "block", fontFamily: SANS, fontSize: "11px", fontWeight: 600, color: C.slateLight, marginBottom: "4px" }}>
                                        TO
                                    </label>
                                    <input
                                        type="date"
                                        value={customTo}
                                        onChange={(e) => setCustomTo(e.target.value)}
                                        style={{
                                            fontFamily: SANS, fontSize: "12.5px", padding: "8px 10px",
                                            borderRadius: "7px", border: `1px solid ${C.border}`, background: C.bg, outline: "none",
                                        }}
                                    />
                                </div>
                                <button
                                    onClick={() => setShowCustom(false)}
                                    style={{
                                        fontFamily: SANS, fontSize: "12.5px", fontWeight: 700, color: "#fff",
                                        background: C.indigo, border: "none", borderRadius: "7px",
                                        padding: "9px 16px", cursor: "pointer", marginTop: "18px",
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    style={{
                                        fontFamily: SANS, fontSize: "13px", fontWeight: 600,
                                        padding: "8px 14px", borderRadius: "8px", border: "none", cursor: "pointer",
                                        color: tab === t.key ? "#fff" : C.slate,
                                        background: tab === t.key ? C.indigo : "transparent",
                                    }}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            <div style={{ position: "relative" }}>
                                <Search size={14} color={C.slateLight} style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)" }} />
                                <input
                                    placeholder="Search customer or note..."
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    style={{
                                        fontFamily: SANS, fontSize: "13px", padding: "9px 12px 9px 32px",
                                        borderRadius: "8px", border: `1px solid ${C.border}`, background: C.bg,
                                        outline: "none", width: "200px",
                                    }}
                                />
                            </div>
                            <div style={{ position: "relative" }}>
                                <select
                                    value={paymentFilter}
                                    onChange={(e) => setPaymentFilter(e.target.value)}
                                    style={{
                                        fontFamily: SANS, fontSize: "13px", padding: "9px 30px 9px 12px",
                                        borderRadius: "8px", border: `1px solid ${C.border}`, background: C.bg,
                                        outline: "none", appearance: "none", color: C.ink, cursor: "pointer",
                                    }}
                                >
                                    <option value="all">All payment statuses</option>
                                    {Object.entries(PAYMENT_META).map(([k, v]) => (
                                        <option key={k} value={k}>{v.label}</option>
                                    ))}
                                </select>
                                <ChevronDown size={13} color={C.slateLight} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                            </div>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "820px" }}>
                            <thead>
                                <tr>
                                    {["CUSTOMER / NOTE", "DATE & TIME", "PAYMENT", "AMOUNT", "STATUS"].map((h, i) => (
                                        <th
                                            key={h}
                                            style={{
                                                textAlign: i === 3 ? "right" : "left",
                                                fontFamily: SANS, fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.06em",
                                                color: C.slate, padding: "13px 20px", borderBottom: `1px solid ${C.border}`,
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={5} style={{ padding: "40px 20px", textAlign: "center", fontFamily: SANS, fontSize: "13.5px", color: C.slateLight }}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                                <Loader2 size={16} className="animate-spin" /> Loading payments...
                                            </span>
                                        </td>
                                    </tr>
                                )}

                                {!loading && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={5} style={{ padding: "40px 20px", textAlign: "center", fontFamily: SANS, fontSize: "13.5px", color: C.slateLight }}>
                                            No transactions match these filters.
                                        </td>
                                    </tr>
                                )}

                                {!loading && filtered.map((r, i) => {
                                    const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                                    return (
                                        <tr key={r.id ?? i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                    <div
                                                        style={{
                                                            width: "34px", height: "34px", borderRadius: "50%",
                                                            background: color, color: "#fff", display: "flex",
                                                            alignItems: "center", justifyContent: "center",
                                                            fontFamily: SANS, fontSize: "12px", fontWeight: 700, flexShrink: 0,
                                                        }}
                                                    >
                                                        {initials(r.user)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontFamily: SANS, fontSize: "13.5px", fontWeight: 600, color: C.ink }}>{r.user}</div>
                                                        <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight }}>{r.course}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontFamily: SANS, fontSize: "13px", color: C.ink }}>{r.date}</div>
                                                <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight }}>{r.time}</div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <PaymentTag status={r.paymentStatus} />
                                            </td>
                                            <td style={{ padding: "14px 20px", textAlign: "right", fontFamily: MONO, fontSize: "13.5px", fontWeight: 700, color: C.ink }}>
                                                ₹{fmtAmount(r.amount)}
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <StatusBadge status={r.status} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}